
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AuthApiService } from '@fleet/api/auth/auth-api.service';
import { DriverApiService } from '@fleet/api/driver/driver-api.service';
import { AuthService } from '@fleet/auth/services';
import { FuseAlertModule, fuseAnimations } from '@fleet/fuse';
import { IssueModel } from '@fleet/model';
import { DialogLayoutModule } from '@fleet/ui/dialog-layout/dialog-layout.module';
import { OnScreenNotification, OnscreenNotificationService } from '@fleet/ui/onscreen-notification/onscreen-notification.service';
import { successNotification } from '@fleet/utilities';

export type ViewMode = 'dialog' | 'standard';

// Custom validator to check if PIN and confirm PIN match
export const checkPinsMatch = (
  control: AbstractControl
): ValidationErrors | null => {
  const parent = control.parent;
  if (!parent) {
    return null;
  }

  const pin = parent.get('pin');
  const confirmPin = control;

  // Only validate if both fields have values
  if (!pin?.value || !confirmPin?.value) {
    return null;
  }

  return pin.value !== confirmPin.value ? { pinsDoNotMatch: true } : null;
};

@Component({
    selector: 'fleet-change-pin',
    template: `<div>
  @switch (viewMode) {
    <!-- Dialog Mode -->
    @case ('dialog') {
      <div>
        <fleet-dialog-layout
          [title]="'Change PIN'"
          [instructions]="'Enter and confirm your new PIN'"
          [iconColour]="'accent'"
          [icon]="'lock'"
          (cancelled)="close()"
          >
          <!-- Main content -->
          <div slot="main">
            @if (changePinForm) {
              <form [formGroup]="changePinForm">
                <div class="grid gap-6 w-full">
                  <div>
                    <mat-form-field
                      [subscriptSizing]="'dynamic'"
                      class="fuse-mat-no-subscript w-full"
                      >
                      <mat-label>New PIN</mat-label>
                      <mat-icon
                        class="icon-size-5"
                        [svgIcon]="'heroicons_solid:lock-closed'"
                        matPrefix
                      ></mat-icon>
                      <input
                        formControlName="pin"
                        required
                        type="password"
                        matInput
                        maxlength="6"
                        />
                      @if (changePinForm.hasError('required', 'pin')) {
                        <mat-error>
                          Please enter your new PIN
                        </mat-error>
                      }
                      @if (changePinForm.hasError('minlength', 'pin')) {
                        <mat-error>
                          PIN must be at least 4 digits
                        </mat-error>
                      }
                      @if (changePinForm.hasError('maxlength', 'pin')) {
                        <mat-error>
                          PIN must be no more than 6 digits
                        </mat-error>
                      }
                    </mat-form-field>
                    <div class="mt-1 text-md text-hint">4-6 digits.</div>
                  </div>
                  <div>
                    <mat-form-field
                      [subscriptSizing]="'dynamic'"
                      class="fuse-mat-no-subscript w-full"
                      >
                      <mat-label>Confirm PIN</mat-label>
                      <mat-icon
                        class="icon-size-5"
                        [svgIcon]="'heroicons_solid:lock-closed'"
                        matPrefix
                      ></mat-icon>
                      <input
                        formControlName="confirmPin"
                        type="password"
                        matInput
                        maxlength="6"
                        />
                      @if (changePinForm.hasError('required', 'confirmPin')) {
                        <mat-error
                          >
                          Please confirm your PIN
                        </mat-error>
                      }
                      @if (
                        changePinForm.hasError('pinsDoNotMatch', 'confirmPin')
                        ) {
                        <mat-error
                          >
                          PINs do not match
                        </mat-error>
                      }
                    </mat-form-field>
                  </div>
                </div>
              </form>
            }
          </div>
          <!-- Error messages -->
          @for (issue of issues(); track issue) {
            <fuse-alert
              slot="error"
              [appearance]="'outline'"
              [showIcon]="true"
              [type]="'error'"
              >
              {{ issue.message }}
            </fuse-alert>
          }
          <!-- Action button -->
          <button
            slot="action"
            type="button"
            mat-flat-button
            class="fleet-mat-button-primary"
            [disabled]="!changePinForm?.valid || loading()"
            (click)="changePin()"
            >
            @if (!loading()) {
              <span>Change PIN</span>
            }
            @if (loading()) {
              <span>Changing...</span>
            }
          </button>
        </fleet-dialog-layout>
      </div>
    }
    <!-- Standard Mode -->
    @default {
      <div>
        @if (changePinForm) {
          <form [formGroup]="changePinForm">
            <div class="grid sm:grid-cols-4 gap-6 w-full">
              <div class="sm:col-span-2">
                <mat-form-field
                  [subscriptSizing]="'dynamic'"
                  class="fuse-mat-no-subscript w-full"
                  >
                  <mat-label>New PIN</mat-label>
                  <mat-icon
                    class="icon-size-5"
                    [svgIcon]="'heroicons_solid:lock-closed'"
                    matPrefix
                  ></mat-icon>
                  <input
                    formControlName="pin"
                    required
                    type="password"
                    matInput
                    maxlength="6"
                    />
                  @if (changePinForm?.hasError('required', 'pin')) {
                    <mat-error>
                      Please enter your new PIN
                    </mat-error>
                  }
                  @if (changePinForm?.hasError('minlength', 'pin')) {
                    <mat-error>
                      PIN must be at least 4 digits
                    </mat-error>
                  }
                  @if (changePinForm?.hasError('maxlength', 'pin')) {
                    <mat-error>
                      PIN must be no more than 6 digits
                    </mat-error>
                  }
                </mat-form-field>
                <div class="mt-1 text-md text-hint">4-6 digits.</div>
              </div>
              <div class="sm:col-span-2">
                <mat-form-field
                  [subscriptSizing]="'dynamic'"
                  class="fuse-mat-no-subscript w-full"
                  >
                  <mat-label>Confirm PIN</mat-label>
                  <mat-icon
                    class="icon-size-5"
                    [svgIcon]="'heroicons_solid:lock-closed'"
                    matPrefix
                  ></mat-icon>
                  <input
                    formControlName="confirmPin"
                    type="password"
                    matInput
                    maxlength="6"
                    />
                  @if (changePinForm?.hasError('required', 'confirmPin')) {
                    <mat-error
                      >
                      Please confirm your PIN
                    </mat-error>
                  }
                  @if (changePinForm.hasError('pinsDoNotMatch', 'confirmPin')) {
                    <mat-error
                      >
                      PINs do not match
                    </mat-error>
                  }
                </mat-form-field>
              </div>
            </div>
            <div class="flex items-center justify-end mt-8">
              <div class="pl-4">
                <button
                  type="button"
                  mat-flat-button
                  class="fleet-mat-button-primary"
                  [disabled]="!changePinForm?.valid || loading()"
                  (click)="changePin()"
                  >
                  @if (!loading()) {
                    <span>Change PIN</span>
                  }
                  @if (loading()) {
                    <span>Changing...</span>
                  }
                </button>
              </div>
            </div>
          </form>
        }
      </div>
    }
  }
</div>`,
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    DialogLayoutModule,
    FuseAlertModule
]
})
export class ChangePinComponent implements OnInit {
  @Input() user: any;
  @Input() viewMode: ViewMode = 'dialog';
  @Output() changeSuccess = new EventEmitter<void>();

  changePinForm: FormGroup;
  fleetProduct: string;
  loading = signal(false);
  issues = signal<IssueModel[]>([]);
  hasChanged = signal(false);

  constructor(
    private fb: FormBuilder,
    private authApiService: AuthApiService,
    private authService: AuthService,
    private driverApiService: DriverApiService,
    private onScreenNotificationService: OnscreenNotificationService,
    @Inject('env') private env: any,
    @Optional() private matDialog: MatDialogRef<ChangePinComponent>,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.fleetProduct = this.env.fleetProduct;
    takeUntilDestroyed();
  }

  ngOnInit(): void {
    this.buildForm();
  }

  buildForm(): void {
    this.changePinForm = this.fb.group({
      pin: [
        '',
        [Validators.required, Validators.minLength(4), Validators.maxLength(6)],
      ],
      confirmPin: [
        '',
        [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(6),
          checkPinsMatch,
        ],
      ],
    });

    // Trigger validation when PIN values change
    this.changePinForm.get('pin')?.valueChanges.subscribe(() => {
      this.changePinForm.get('confirmPin')?.updateValueAndValidity();
      this.changeDetectorRef.markForCheck();
    });
  }

  changePin(): void {
    if (!this.changePinForm.valid) return;

    this.loading.set(true);
    this.issues.set([]);

    const payload = {
      pin: this.changePinForm.get('pin')?.value,
    };

    // Check if we're changing PIN for a different user (admin/supervisor setting PIN for driver)
    const currentUser = this.authService.userValue;
    const isSettingPinForOtherUser =
      this.user?.username &&
      currentUser?.username &&
      this.user.username !== currentUser.username;

    // Choose the appropriate API call
    const apiCall$ = isSettingPinForOtherUser
      ? this.driverApiService.setPin(this.user.driverId, payload)
      : this.authApiService.changePin(payload);

    // Determine notification content
    const notificationContent = isSettingPinForOtherUser
      ? {
          title: 'PIN Set Successfully',
          subTitle: `PIN has been successfully set for ${this.user.username}`,
        }
      : {
          title: 'PIN Changed Successfully',
          subTitle: 'Your PIN has been successfully changed',
        };

    // Execute the API call
    apiCall$.subscribe({
      next: (response: any) => {
        this.handleSuccess(notificationContent);
      },
      error: (error: any) => {
        this.handleError(error);
      },
    });
  }

  private handleSuccess(notificationContent: {
    title: string;
    subTitle: string;
  }): void {
    this.loading.set(false);
    this.hasChanged.set(true);

    this.onScreenNotificationService.setNotification({
      ...successNotification,
      ...notificationContent,
    } as OnScreenNotification);

    this.changeSuccess.emit();

    // Close dialog if in dialog mode
    if (this.viewMode === 'dialog' && this.matDialog) {
      this.matDialog.close();
    }

    this.changeDetectorRef.markForCheck();
  }

  private handleError(error: any): void {
    this.loading.set(false);
    this.issues.set(error);
    this.changeDetectorRef.markForCheck();
  }

  close(): void {
    if (this.matDialog) {
      this.matDialog.close();
    }
  }
}
